// src/components/ProtectedRoute.js - WITH PERSISTENT LOCALSTORAGE CACHE
import React, { useState, useEffect, createContext, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// Cache configuration
const CACHE_DURATION = 12 * 60 * 60 * 1000; // 12 hours

// Helper functions for localStorage cache with user-specific keys
const getCacheKey = (userId) => `approval_status_${userId}`;

const getCachedApproval = (userId) => {
  try {
    const cacheKey = getCacheKey(userId);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { approved, timestamp } = JSON.parse(cached);
    
    // Check if cache is still valid
    if (timestamp && (Date.now() - timestamp) < CACHE_DURATION) {
      return { approved, timestamp };
    }
    
    // Clear expired cache
    localStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    // Clear corrupted cache
    try {
      localStorage.removeItem(getCacheKey(userId));
    } catch (e) {
      // Ignore cleanup errors
    }
    return null;
  }
};

const setCachedApproval = (userId, approved) => {
  try {
    const cacheKey = getCacheKey(userId);
    const cacheData = {
      approved,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    // localStorage might be full or disabled
    console.warn('Failed to cache approval status');
  }
};

const clearCachedApproval = (userId = null) => {
  try {
    if (userId) {
      // Clear specific user's cache
      localStorage.removeItem(getCacheKey(userId));
    } else {
      // Clear all approval caches (for logout)
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('approval_status_')) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    // Ignore localStorage errors
  }
};

// Create context for sharing approval status
const ApprovalContext = createContext();

// Approval Provider Component
export const ApprovalProvider = ({ children }) => {
  const [approvalStatus, setApprovalStatus] = useState({
    user: null,
    approved: false,
    checked: false,
    loading: false
  });

  const checkApproval = async (user, forceCheck = false) => {
    if (!user) {
      setApprovalStatus({ 
        user: null, 
        approved: false, 
        checked: true, 
        loading: false
      });
      clearCachedApproval();
      return;
    }

    // Check localStorage cache first
    if (!forceCheck) {
      const cachedApproval = getCachedApproval(user.id);
      if (cachedApproval !== null) {
        setApprovalStatus({
          user,
          approved: cachedApproval.approved,
          checked: true,
          loading: false
        });
        return; // Use cached result - no API call needed
      }
    }

    // No valid cache found - make API call
    setApprovalStatus(prev => ({ ...prev, loading: true }));

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const response = await fetch(`${apiUrl}/profiles/${user.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      if (!response.ok) {
        const approved = false;
        setCachedApproval(user.id, approved);
        setApprovalStatus({ 
          user, 
          approved, 
          checked: true, 
          loading: false
        });
        return;
      }

      const approvalData = await response.json();
      const approved = approvalData.approved || false;
      
      // Cache the result
      setCachedApproval(user.id, approved);
      
      setApprovalStatus({
        user,
        approved,
        checked: true,
        loading: false
      });
      
    } catch (error) {
      const approved = false;
      setCachedApproval(user.id, approved);
      setApprovalStatus({ 
        user, 
        approved, 
        checked: true, 
        loading: false
      });
    }
  };

  const clearApprovalStatus = () => {
    setApprovalStatus({ 
      user: null, 
      approved: false, 
      checked: false, 
      loading: false
    });
    clearCachedApproval(); // Clear all approval caches
  };

  // Only listen for logout to clear cache
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        clearApprovalStatus();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <ApprovalContext.Provider value={{ 
      approvalStatus, 
      checkApproval, 
      clearApprovalStatus
    }}>
      {children}
    </ApprovalContext.Provider>
  );
};

// Hook to use approval context
const useApproval = () => {
  const context = useContext(ApprovalContext);
  if (!context) {
    throw new Error('useApproval must be used within ApprovalProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const { approvalStatus, checkApproval } = useApproval();
  const location = useLocation();

  // Auth check - runs only once on mount
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        
        if (user) {
          // Check localStorage cache immediately
          const cachedApproval = getCachedApproval(user.id);
          
          if (cachedApproval !== null) {
            // Cache found - checkApproval will use it and update state properly
            await checkApproval(user, false);
          } else {
            // No cache - need to check approval
            await checkApproval(user);
          }
        }
      } catch (error) {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };

    getUser();
  }, []); // Run only once on mount

  // Show loading only during initial auth check
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if no user
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check localStorage cache for immediate rendering
  const cachedApproval = getCachedApproval(user.id);
  
  if (cachedApproval !== null && cachedApproval.approved) {
    // User is approved based on valid cache - render immediately
    return children;
  }

  // Show loading while checking approval (only if no cache)
  if (approvalStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking account status...</p>
        </div>
      </div>
    );
  }

  // Show pending approval for unapproved users (either fresh check or cached)
  if ((approvalStatus.checked && !approvalStatus.approved) || 
      (cachedApproval !== null && !cachedApproval.approved)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">Account Pending Approval</h2>
          <p className="mb-6 text-gray-600">
            Your account is currently pending approval. An administrator will review and approve your account soon.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => checkApproval(user, true)}
              disabled={approvalStatus.loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center"
            >
              {approvalStatus.loading ? (
                <>
                  <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Checking...
                </>
              ) : (
                'Check Approval Status'
              )}
            </button>
            <button
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = '/login';
              }}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Sign Out
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            If you believe this is an error, please contact support.
          </p>
        </div>
      </div>
    );
  }

  // If no cache and approval hasn't been checked yet, show loading
  if (!approvalStatus.checked && cachedApproval === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking account status...</p>
        </div>
      </div>
    );
  }

  // User is authenticated and approved - render protected content
  return children;
};

export default ProtectedRoute;